import React, { useState, useRef, useEffect, useContext } from "react";
import { useIntl } from "react-intl";
import { Text, Spin, Tooltip } from "react_reusable_components";

import Graph from "../DashboardGraphs/Graphs/TotalAuditvsCompleted/Graph";
import { TwoColumn, TwoRow } from "../../../core/layouts";
import variables from "../../../themes/base/styles/variables";
import {
  getConditionalContent,
  getDashboardQueryParams,
  getLocalizedMessage,
} from "../../../UtilityFunctions/utils";
import TableActions from "../../../components/TableActions";
import DashboardService from "../../../services/dashboard-service";
import { DashboardContext } from "../../../globalContext/dashboard/dashboardProvider";
import useResponsive from "../../../core/hooks/useResponsive";
import { GlobalLoaderContext } from "../../../globalContext/globalLoader/globalLoaderProvider";
import { BAD_REQUEST } from "../../../constants/errorConstants";
import { InfoCircleOutlined } from "@ant-design/icons";
import { GRAPH_TOOLTIP } from "../../../constants";
import GraphTooltipContainer from "../../GraphTooltipContainer";

export const TotalCompleteAuditStyles = {
  auditsBox: {
    border: variables.border,
    padding: variables.paddingSmall,
    backgroundColor: variables.bodyBg,
    minHeight: variables.graphsMinHeight,
  },
  headerSection: {
    justifyContent: variables.spaceBetween,
  },
  headerLeftSection: {
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeXlarge,
  },
  bottomSection: {
    paddingTop: variables.paddingSmall,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    display: variables.dFlex,
  },
  spinner: {
    justifyContent: variables.alignCenter,
    paddingTop: variables.paddingTop,
  },
  switch: {
    gap: variables.gapXS1,
    fontWeight: variables.switchLabelFontWeight,
    spaceBetween: variables.spaceBetween,
    alignItems: variables.alignCenter,
  },
  mobileSwitch: {
    fontSize: variables.fontSizeSmall,
    iconWidth: variables.iconWidth,
  },
  notFoundText: {
    fontSize: variables.fontSizeXxlarge,
    textAlign: variables.alignCenter,
    paddingTop: variables.paddingTop2,
    width: variables.fullWidth,
  },
  mobileHeading: {
    whiteSpace: variables.whiteSpace,
    overflow: variables.overflowHidden,
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeXlarge,
  },
  tooltipWidth: { width: variables.tooltipWidth },
};

const TotalCompleteAudits = ({ filter }) => {
  const responsive = useResponsive();
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [dataNotFound, setDataNotFound] = useState("");
  const [
    TotalCompleteAuditsVsTotalAuditMonthData,
    SetTotalCompleteAuditsVsTotalAuditMonthData,
  ] = useState([]);
  const [dashboardState, dashboardDispatch] = useContext(DashboardContext);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const screenWidth = window.innerWidth;

  const Months = [
    {
      name: "Apr",
      key: 3,
    },
    {
      name: "May",
      key: 4,
    },
    {
      name: "Jun",
      key: 5,
    },
    {
      name: "Jul",
      key: 6,
    },
    {
      name: "Aug",
      key: 7,
    },
    {
      name: "Sept",
      key: 8,
    },
    {
      name: "Oct",
      key: 9,
    },
    {
      name: "Nov",
      key: 10,
    },
    {
      name: "Dec",
      key: 11,
    },
    {
      name: "Jan",
      key: 0,
    },
    {
      name: "Feb",
      key: 1,
    },
    {
      name: "Mar",
      key: 2,
    },
  ];
  const yAxisTicks = [0, 100, 200, 300, 400];

  useEffect(() => {
    setIsLoading(true);
    let params = getDashboardQueryParams(filter);
    delete params.isPercentage;
    delete params.subCategories;
    DashboardService.getTotalAuditVsCompleteAuditData({
      dispatch: dashboardDispatch,
      intl,
      params,
    }).catch((error) => {
      if (error?.response?.data?.code === BAD_REQUEST) {
        setDataNotFound(getLocalizedMessage(intl, "dataNotAvailable.label"));
      }
    });
    setIsLoading(false);
  }, [filter]);

  useEffect(() => {
    if (dashboardState.totalAuditVsCompleteAudit?.monthData) {
      const dashMonthData =
        dashboardState?.totalAuditVsCompleteAudit?.monthData;
      const newData = Months.map(({ key, name }) => ({
        name,
        "Planned Operational Audits": dashMonthData.find(
          (item) => item.plannedMonth === key + 1
        )?.operationalAudit,
        "Planned Repair Audits": dashMonthData.find(
          (item) => item.plannedMonth === key + 1
        )?.repairAudit,
        "Operational Completed Audit": dashMonthData.find(
          (item) => item.plannedMonth === key + 1
        )?.operationalCompletedAudit,
        "Repair Completed Audit": dashMonthData.find(
          (item) => item.plannedMonth === key + 1
        )?.repairCompletedAudit,
        "Total Audit": dashMonthData.find(
          (item) => item.plannedMonth === key + 1
        )?.totalAudit,
        "Total Completed Audit": dashMonthData.find(
          (item) => item.plannedMonth === key + 1
        )?.totalCompletedAudit,
      }));
      SetTotalCompleteAuditsVsTotalAuditMonthData(newData);
    }
  }, [dashboardState]);

  const [graphMounted, setGraphMounted] = useState(false);

  // Use a useRef to access the Graph component
  const graphRef = useRef();

  useEffect(() => {
    setGraphMounted(true);
  }, []);

  const downloadChartImage = () => {
    // Call the downloadChartImage function of the Graph component only if it is mounted
    try {
      if (graphMounted && graphRef.current) {
        graphRef.current.downloadChartImage();
      }
    } catch (error) {
      setDataNotFound(error);
    }
  };

  const downloadUserReportsHandler = () => {
    let queryParams = getDashboardQueryParams(filter);
    delete queryParams.isPercentage;
    DashboardService.dashboardDownloadReportApiCall({
      params: {
        ...queryParams,
        fileName: getLocalizedMessage(
          intl,
          "dashboard.totalAuditVsCompleteAudit.label"
        ),
        reportType: getLocalizedMessage(intl, "auditReport.label"),
      },
      intl,
      loaderDispatch: loaderDispatch,
    }).catch((error) => {
      if (error?.response?.data?.code === BAD_REQUEST) {
        setDataNotFound("Data Not Available!");
      }
    });
  };

  return (
    <TwoRow
      id="planned-graph"
      style={TotalCompleteAuditStyles.auditsBox}
      topSection={
        <TwoColumn
          style={TotalCompleteAuditStyles.headerSection}
          leftSection={
            responsive.isMd ? (
              <Text
                type="secondary"
                style={TotalCompleteAuditStyles.headerLeftSection}
              >
                {getLocalizedMessage(
                  intl,
                  "dashboard.totalAuditVsCompleteAudit.label"
                )}{" "}
                <Tooltip
                  overlayInnerStyle={TotalCompleteAuditStyles.tooltipWidth}
                  title={
                    <GraphTooltipContainer
                      data={
                        GRAPH_TOOLTIP(intl).PLANNED_VS_COMPLETE_AUDIT_MONTH_VISE
                      }
                    />
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Text>
            ) : (
              <Text
                type="secondary"
                style={TotalCompleteAuditStyles.mobileHeading}
              >
                {getLocalizedMessage(
                  intl,
                  "dashboard.totalAuditVsCompleteAudit.label"
                )}{" "}
                <Tooltip
                  overlayInnerStyle={TotalCompleteAuditStyles.tooltipWidth}
                  title={
                    <GraphTooltipContainer
                      data={
                        GRAPH_TOOLTIP(intl).PLANNED_VS_COMPLETE_AUDIT_MONTH_VISE
                      }
                    />
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Text>
            )
          }
          rightSection={getConditionalContent({
            condition: dataNotFound,
            trueValue: null,
            falseValue: (
              <TableActions
                tableActionsOptions={[
                  {
                    label: getLocalizedMessage(intl, "downloadGraph.label"),
                    action: downloadChartImage,
                  },
                  {
                    label: getLocalizedMessage(intl, "downloadReport.label"),
                    action: downloadUserReportsHandler,
                  },
                ]}
              />
            ),
          })}
        />
      }
      bottomSectionStyle={getConditionalContent({
        condition: isLoading,
        trueValue: TotalCompleteAuditStyles.spinner,
        falseValue: TotalCompleteAuditStyles.bottomSection,
      })}
      bottomSection={
        getConditionalContent({
          condition: isLoading,
          trueValue: <Spin size={"large"} type={"default"} />,
          falseValue: (
            <Graph
              ref={graphRef}
              id="graph"
              data={TotalCompleteAuditsVsTotalAuditMonthData}
              yAxisTicks={yAxisTicks}
              width="100%"
              height={getConditionalContent({
                condition: screenWidth < 768,
                trueValue: 310,
                falseValue: 400,
              })}
              barSize={12}
              dataKey1="name"
              axisLine={false}
              dataKey2={"Operational Completed Audit"}
              fill1={variables.lightBlue}
              dataKey3={getLocalizedMessage(
                intl,
                "operationalAuditsDataKey.label"
              )}
              dataKey4={getLocalizedMessage(
                intl,
                "repairCompleteAuditDataKey.label"
              )}
              dataKey5={getLocalizedMessage(intl, "repairAuditDataKey.label")}
              dataKey6={getLocalizedMessage(intl, "totalAuditDataKey.label")}
              dataKey7={getLocalizedMessage(
                intl,
                "totalCompletedAuditDataKey.label"
              )}
              fill2={variables.green}
              style={{ fontWeight: 400, color: variables.textDefault }}
              linkLabel={getLocalizedMessage(
                intl,
                "totalAuditVsCompleted.link.label"
              )}
              colorBar1={variables.primaryBg}
              colorBar2={variables.coral}
              colorBar3={variables.cobaltBlue}
              colorBar4={variables.indigo}
              selectedAuditType={filter?.auditType?.[0]}
            />
          ),
        })
      }
    />
  );
};

export default TotalCompleteAudits;
